import React, { useContext, useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import {
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import PropTypes from 'prop-types';

import { EXT_PLAN_FREQUENCIES } from 'global-constants/constants';
import { CPT_DISPLAY_LIST } from 'Views/Common/enum';
import { PlansContext } from 'Views/Plans/context';
import {
  CPT_UPDATED,
  SET_SELECTED_TEMPLATE,
  UPDATE_FIELDS_VALIDITY,
} from 'Views/Plans/context/actions';
import { SectionHeader, Select } from 'components';
import styles from './CPTValues.module.scss';
import ImportDropdownButton from '../ImportDropdownButton/ImportDropdownButton';
import CPTDnDTable from './CPTDnDTable';

// Standalone render functions (unchanged from original)
function renderListCell({ value, rowIndex, cpt_codes }) {
  return (
    <div
      style={{
        width: '100%',
        textAlign: 'center',
        fontWeight: 400,
        fontSize: '13.5px',
        color: '#475670',
        letterSpacing: '0.5px',
        padding: '8px 0',
      }}
    >
      {cpt_codes[rowIndex].list}
    </div>
  );
}

function renderTextInput({
  value,
  rowIndex,
  cpt_codes,
  renderTableInput,
  name,
  textFieldProps,
}) {
  return renderTableInput(
    cpt_codes[rowIndex][name],
    name,
    rowIndex,
    textFieldProps,
    true,
    cpt_codes[rowIndex].list,
  );
}

function renderStatusSelectCell({
  value,
  rowIndex,
  cpt_codes,
  renderStatusSelect,
}) {
  return renderStatusSelect(
    cpt_codes[rowIndex].cpt_display,
    'cpt_display',
    rowIndex,
  );
}

function renderActionsCell({
  rowIndex,
  addToList,
  deleteCPTCodes,
  cpt_codes,
  styles,
  has_subscriptions,
}) {
  return (
    <div className="d-flex flex-no-wrap">
      <IconButton
        color="primary"
        title="Add to List"
        aria-label="add-to-list"
        onClick={() => addToList(rowIndex)}
        data-testid={`add-to-list-button-${rowIndex}`}
      >
        <AddRoundedIcon fontSize="inherit" />
      </IconButton>
      {rowIndex > 0 && (
        <IconButton
          aria-label="delete"
          onClick={() => deleteCPTCodes(rowIndex, cpt_codes[rowIndex].list)}
          data-testid={`delete-cpt-button-${rowIndex}`}
          classes={{ root: styles.deleteIconColor }}
        >
          <DeleteIcon fontSize="inherit" />
        </IconButton>
      )}
    </div>
  );
}

function renderFrequencyCell(
  value,
  rowIndex,
  cpt_codes,
  has_subscriptions,
  EXT_PLAN_FREQUENCIES,
  updateValues,
) {
  const isSameList = Boolean(
    rowIndex && cpt_codes[rowIndex - 1].list === cpt_codes[rowIndex].list,
  );
  if (isSameList) return null;
  return (
    <FormControl
      fullWidth
      margin="dense"
      variant="outlined"
      disabled={has_subscriptions}
    >
      <Select
        id="cpt-select-frequency"
        value={cpt_codes[rowIndex].frequency}
        name="frequency"
        onChange={(event) =>
          updateValues(
            event.target.name,
            event.target.value,
            rowIndex,
            true,
            cpt_codes[rowIndex].list,
          )
        }
      >
        {Object.keys(EXT_PLAN_FREQUENCIES).map((item, itemIndex) => (
          <MenuItem
            key={`cpt-frequency-select-item-${itemIndex + 1}`}
            value={item}
            data-testid={`cpt-frequency-select-item-${itemIndex}`}
          >
            {EXT_PLAN_FREQUENCIES[item]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// Drag Overlay Content Component
const DragOverlayContent = ({
  activeId,
  cpt_codes,
  listGroups,
  tableColumns,
}) => {
  if (!activeId || !cpt_codes.length) return null;

  // Find the active list group
  const activeGroup = listGroups.find((group) => group.id === activeId);
  if (!activeGroup) return null;

  // Get the first row of the group for display
  const firstRowIndex = activeGroup.startIndex;
  const firstRow = cpt_codes[firstRowIndex];
  if (!firstRow) return null;
};

DragOverlayContent.propTypes = {
  activeId: PropTypes.string,
  cpt_codes: PropTypes.array.isRequired,
  listGroups: PropTypes.array.isRequired,
  tableColumns: PropTypes.array.isRequired,
};

const CPTValues = ({ editMode = false }) => {
  const {
    state: {
      productInfo = {},
      cpt_codes,
      default_cpt_value,
      isValid,
      highlightErrors,
      plan_id,
      has_subscriptions,
      selectedGroup,
      templates,
      
    },
    dispatch,
  } = useContext(PlansContext);
  const vet_only = selectedGroup.industry === 'Veterinary';
  const addonPlan = productInfo?.plan_package_type === 'Addon';

  useEffect(() => {
    if (!plan_id || (addonPlan && !has_subscriptions && cpt_codes.length === 0))
      dispatchCPTValues([default_cpt_value]);
  }, [dispatch]);

  const checkCptValidity = (code) =>
    Object.keys(code).some(
      (key) => key !== 'duplicate_code' && !code?.[key]?.toString().length,
    );

  useEffect(() => {
    let isFieldsError = false;

    if (cpt_codes.length === 1) {
      /* One CPT row & changes made */
      if (!_.isEqual(cpt_codes?.[0], default_cpt_value)) {
        isFieldsError = checkCptValidity(cpt_codes?.[0]);
      }
    } else {
      /* Multiple CPT rows & has all values */
      isFieldsError = cpt_codes.some((code) => checkCptValidity(code));
    }

    dispatch({
      type: UPDATE_FIELDS_VALIDITY,
      payload: {
        isValid: { ...isValid, cpt: !isFieldsError },
      },
    });
  }, [highlightErrors, cpt_codes]);

  const dispatchCPTValues = (updated_cpt_codes) =>
    dispatch({
      type: CPT_UPDATED,
      payload: { cpt_codes: updated_cpt_codes },
    });
  const addCPTCodes = () =>
    dispatchCPTValues(
      cpt_codes.concat([
        {
          ...default_cpt_value,
          list: cpt_codes.length
            ? cpt_codes[cpt_codes.length - 1]?.list + 1
            : 1,
        },
      ]),
    );

  const [collapsedGroups, setCollapsedGroups] = useState({});

  const handleCollapseGroup = (listNumber) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [listNumber]: true,
    }));
  };

  const handleExpandGroup = (listNumber) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [listNumber]: false,
    }));
  };

  // Enhanced helper function to group rows by list number with stable IDs
  const listGroups = useMemo(() => {
    const listMap = new Map();
    cpt_codes.forEach((code, index) => {
      if (!listMap.has(code.list)) {
        listMap.set(code.list, []);
      }
      listMap.get(code.list).push({ ...code, originalIndex: index });
    });
    return Array.from(listMap.entries())
      .map(([listNumber, rows]) => ({
        listNumber,
        rows,
        startIndex: Math.min(...rows.map((r) => r.originalIndex)),
        id: `list-${listNumber}-${rows[0]?.code || ''}-${rows.length}`,
        height: rows.length * 60, // Consistent height calculation
      }))
      .sort((a, b) => a.startIndex - b.startIndex);
  }, [cpt_codes]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const templatesOptions =
    templates?.data?.map((template) => template.name) || [];

  const handleSelectOption = (option) => {
    setSelectedOption(option);
    const hasNonDefaultCPT =
      cpt_codes?.length &&
      JSON.stringify(cpt_codes[0]) !== JSON.stringify(default_cpt_value);

    if (hasNonDefaultCPT) {
      setConfirmOpen(true);
    } else {
      dispatch({ type: SET_SELECTED_TEMPLATE, payload: { option } });
    }
  };

  const handleConfirm = () => {
    if (selectedOption) {
      dispatch({
        type: SET_SELECTED_TEMPLATE,
        payload: { option: selectedOption },
      });
    }
    setConfirmOpen(false);
    setSelectedOption(null);
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setSelectedOption(null);
  };

  const addToList = (index) => {
    const newCodes = _.cloneDeep(cpt_codes);
    const selectedCode = newCodes[index];
    const { list, frequency, quantity, total_unit, pdt_name } = selectedCode;

    let overrideValues = {
      quantity,
      total_unit,
    };

    if (frequency === 'unlimited')
      overrideValues = {
        quantity: 'inf',
        total_unit: 'inf',
      };

    newCodes.splice(index + 1, 0, {
      ...default_cpt_value,
      list,
      frequency,
      pdt_name,
      ...overrideValues,
    });

    dispatchCPTValues(newCodes);
  };

  const deleteCPTCodes = (rowIndex, deletedList) => {
    const shouldUpdateLists =
      cpt_codes.filter((item) => item.list === deletedList).length === 1;

    dispatchCPTValues(
      cpt_codes
        .filter((_code, index) => index !== rowIndex)
        .map(({ list, ...rest }) => ({
          ...rest,
          list: shouldUpdateLists && list > deletedList ? list - 1 : list,
        })),
    );
  };

  const updateValues = (name, value, rowIndex, shouldUpdateList, list) => {
    if (name === 'frequency' && value === 'unlimited') {
      dispatchCPTValues(
        cpt_codes.map((code, index) =>
          rowIndex === index || (shouldUpdateList && list === code.list)
            ? {
                ...code,
                [name]: value,
                quantity: 'inf',
                total_unit: 'inf',
              }
            : code,
        ),
      );
      return;
    } else if (name === 'frequency') {
      dispatchCPTValues(
        cpt_codes.map((code, index) =>
          rowIndex === index || (shouldUpdateList && list === code.list)
            ? {
                ...code,
                [name]: value,
                quantity: code?.quantity === 'inf' ? '' : code?.quantity || '',
                total_unit:
                  code?.total_unit === 'inf' ? '' : code?.total_unit || '',
              }
            : code,
        ),
      );
      return;
    }

    dispatchCPTValues(
      cpt_codes.map((code, index) =>
        rowIndex === index || (shouldUpdateList && list === code.list)
          ? {
              ...code,
              [name]: value,
            }
          : code,
      ),
    );
  };

  const getValue = ({ target: { name, type, value } }) => {
    const allowDecimals =
      name === 'retail_price' || name === 'subscription_price';

    if (type === 'number') {
      let parsedValue;

      /* Allow decimal values for Retail and Subscription price */
      if (allowDecimals)
        parsedValue = Math.abs(parseFloat(+value.replace(/^[/D.]/gi, '')));
      else parsedValue = Math.abs(parseFloat(+value.replace(/^[/D]/gi, '')));

      return isNaN(parsedValue) ? 0 : parsedValue;
    }

    return value;
  };

  const handleFocus = (event) => event.target.select();

  const commonCellHeaderProps = (className) => () => ({ className });

  const tableNumberFieldProps = {
    inputProps: {
      type: 'number',
      onWheel: (event) => event.target.blur(),
    },
    onFocus: handleFocus,
  };

  const renderTableInput = (
    value,
    name,
    rowIndex,
    textFieldProps,
    shouldUpdateList,
    list,
    required = true,
  ) => (
    <TextField
      required
      variant="outlined"
      margin="dense"
      value={value}
      name={name}
      error={Boolean(
        required &&
          highlightErrors &&
          !isValid?.cpt &&
          !value &&
          !value?.toString?.length,
      )}
      onChange={(event) =>
        updateValues(
          event.target.name,
          getValue(event),
          rowIndex,
          shouldUpdateList,
          list,
        )
      }
      inputProps={{
        'data-testid': `cpt-${name}`,
      }}
      {...textFieldProps}
    />
  );

  const renderStatusSelect = (value, name, rowIndex) => (
    <FormControl fullWidth margin="dense" variant="outlined">
      <Select
        id="cpt-select-display"
        value={value}
        name={name}
        onChange={(event) =>
          updateValues(event.target.name, event.target.value, rowIndex)
        }
      >
        {Object.keys(CPT_DISPLAY_LIST).map((item, itemIndex) => (
          <MenuItem
            key={itemIndex}
            value={item}
            data-testid={`cpt-display-select-item-${itemIndex + 1}`}
          >
            {CPT_DISPLAY_LIST[item]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // Build tableColumns array in a clear, stepwise way
  const baseColumns = [
    {
      name: '',
      label: '',
      options: {
        setCellHeaderProps: commonCellHeaderProps(styles.iconColumn),
        sort: false,
        filter: false,
        customBodyRenderLite: () => null,
      },
    },
    {
      name: 'list',
      label: 'List',
      options: {
        setCellHeaderProps: commonCellHeaderProps(styles.inputColumn),
        customBodyRender: (value, tableMeta) =>
          renderListCell({ value, rowIndex: tableMeta.rowIndex, cpt_codes }),
      },
    },
    {
      name: 'pdt_name',
      label: 'Category *',
      options: {
        setCellHeaderProps: commonCellHeaderProps(styles.longColumn),
        customBodyRender: (value, { rowIndex, rowData }) => {
          const isSameList = Boolean(
            rowIndex &&
              cpt_codes[rowIndex - 1].list === cpt_codes[rowIndex].list,
          );

          return (
            !isSameList &&
            renderTableInput(
              value,
              'pdt_name',
              rowIndex,
              {
                className: styles.longColumn,
              },
              true,
              cpt_codes[rowIndex].list,
            )
          );
        },
      },
    },
  ];

  // Conditionally add the standard_code column
  const standardCodeColumn = vet_only
    ? [
        {
          name: 'standard_code',
          label: 'Standard Code *',
          options: {
            setCellHeaderProps: commonCellHeaderProps(styles.mediumColumn),
            customBodyRender: (value, { rowIndex }) =>
              renderTableInput(value, 'standard_code', rowIndex, {
                className: styles.mediumColumn,
              }),
          },
        },
      ]
    : [];

  // The rest of the columns
  const restColumns = [
    {
      name: 'code',
      label: 'Code *',
      options: {
        setCellHeaderProps: commonCellHeaderProps(styles.mediumColumn),
        customBodyRender: (value, { rowIndex }) =>
          renderTableInput(value, 'code', rowIndex, {
            className: styles.mediumColumn,
          }),
      },
    },
    {
      name: 'description',
      label: 'Description *',
      options: {
        setCellHeaderProps: commonCellHeaderProps(styles.longColumn),
        customBodyRender: (value, { rowIndex }) =>
          renderTableInput(value, 'description', rowIndex, {
            className: styles.longColumn,
          }),
      },
    },
    {
      name: 'retail_price',
      label: 'Retail Price',
      options: {
        setCellHeaderProps: commonCellHeaderProps(styles.inputColumn),
        customBodyRender: (value, { rowIndex }) =>
          renderTableInput(value, 'retail_price', rowIndex, {
            className: styles.mediumColumn,
            InputProps: {
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            },
            ...tableNumberFieldProps,
          }),
      },
    },
    {
      name: 'subscription_price',
      label: 'Subscription Price',
      options: {
        setCellHeaderProps: commonCellHeaderProps(styles.inputColumn),
        customBodyRender: (value, { rowIndex }) =>
          renderTableInput(value, 'subscription_price', rowIndex, {
            className: styles.mediumColumn,
            InputProps: {
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            },
            ...tableNumberFieldProps,
            disabled: has_subscriptions,
          }),
      },
    },
    {
      name: 'frequency',
      label: 'Frequency *',
      options: {
        setCellHeaderProps: commonCellHeaderProps(styles.inputColumn),
        customBodyRender: (value, { rowIndex }) =>
          renderFrequencyCell(
            value,
            rowIndex,
            cpt_codes,
            has_subscriptions,
            EXT_PLAN_FREQUENCIES,
            updateValues,
          ),
      },
    },
    {
      name: 'quantity',
      label: 'Allowed Units *',
      options: {
        setCellHeaderProps: commonCellHeaderProps(styles.inputColumn),
        customBodyRender: (value, tableMeta) => {
          const isSameList = Boolean(
            tableMeta.rowIndex &&
              cpt_codes[tableMeta.rowIndex - 1].list ===
                cpt_codes[tableMeta.rowIndex].list,
          );
          return (
            !isSameList &&
            renderTextInput({
              value,
              rowIndex: tableMeta.rowIndex,
              cpt_codes,
              renderTableInput,
              name: 'quantity',
              textFieldProps: {
                ...tableNumberFieldProps,
                className: styles.inputColumn,
                disabled:
                  has_subscriptions ||
                  cpt_codes[tableMeta.rowIndex]?.frequency === 'unlimited',
              },
            })
          );
        },
      },
    },
    {
      name: 'total_unit',
      label: 'Total Units *',
      options: {
        setCellHeaderProps: commonCellHeaderProps(styles.inputColumn),
        customBodyRender: (value, tableMeta) => {
          const isSameList = Boolean(
            tableMeta.rowIndex &&
              cpt_codes[tableMeta.rowIndex - 1].list ===
                cpt_codes[tableMeta.rowIndex].list,
          );
          return (
            !isSameList &&
            renderTextInput({
              value,
              rowIndex: tableMeta.rowIndex,
              cpt_codes,
              renderTableInput,
              name: 'total_unit',
              textFieldProps: {
                ...tableNumberFieldProps,
                className: styles.inputColumn,
                disabled:
                  has_subscriptions ||
                  cpt_codes[tableMeta.rowIndex]?.frequency === 'unlimited',
              },
            })
          );
        },
      },
    },
    {
      name: 'cpt_display',
      label: 'Display *',
      options: {
        setCellHeaderProps: commonCellHeaderProps(styles.inputColumn),
        customBodyRender: (value, tableMeta) =>
          renderStatusSelectCell({
            value,
            rowIndex: tableMeta.rowIndex,
            cpt_codes,
            renderStatusSelect,
          }),
      },
    },
    {
      name: 'actions',
      label: ' ',
      options: {
        setCellHeaderProps: commonCellHeaderProps(styles.iconColumn),
        display: !has_subscriptions,
        customBodyRender: (value, tableMeta) =>
          renderActionsCell({
            rowIndex: tableMeta.rowIndex,
            addToList,
            deleteCPTCodes,
            cpt_codes,
            styles,
            has_subscriptions,
          }),
      },
    },
  ];

  // Combine all columns
  const tableColumns = [...baseColumns, ...standardCodeColumn, ...restColumns];

  return (
    <>
      <SectionHeader
        title="Inclusions"
        gutterBottom
        titleVariant="h5"
        titleComponent="h5"
        rightSection={
          !has_subscriptions && (
            <ImportDropdownButton
              options={templatesOptions}
              onSelectOption={handleSelectOption}
              isConfirmOpen={confirmOpen}
              onConfirm={handleConfirm}
              onCancel={handleCancel}
              onAdd={addCPTCodes}
              confirmModalOptions={{
                title: 'Override CPT Codes?',
                description:
                  'You already have CPT codes in your plan. Importing a new template will override them. Do you want to continue?',
                confirmText: 'Yes, Import',
                cancelText: 'No, Cancel',
              }}
              editMode={Boolean(plan_id)}
            />
          )
        }
      />
      <Divider />
      <CPTDnDTable
        cpt_codes={cpt_codes}
        tableColumns={tableColumns}
        dispatchCPTValues={dispatchCPTValues}
        has_subscriptions={has_subscriptions}
        collapsedGroups={collapsedGroups}
        listGroups={listGroups}
        styles={styles}
        addToList={addToList}
        deleteCPTCodes={deleteCPTCodes}
        onCollapseGroup={handleCollapseGroup}
        onExpandGroup={handleExpandGroup}
      />
      {Boolean(highlightErrors && !isValid.cpt) && (
        <Typography className={styles.errorText} color="error" align="right">
          * Please fill the required fields
        </Typography>
      )}
    </>
  );
};

CPTValues.propTypes = {
  editMode: PropTypes.bool,
};

export default CPTValues;
